/**
 * Utility functions for Solution Import operations
 * 
 * This file contains pure helper functions extracted from SolutionImportModal
 * to improve code organization and testability.
 */

/**
 * Check if a field needs to be created
 * Returns true if the value is empty or starts with "NEW:"
 */
export const needsCreation = (value: any): boolean => {
  if (!value) return true;
  if (typeof value === "string") {
    return value.trim() === "" || value.startsWith("NEW:");
  }
  return false;
};

/**
 * Helper function to check if a field is empty/unmapped (for UI validation)
 * Returns true if the value is empty but doesn't have a "NEW:" prefix
 */
export const isEmptyOrUnmapped = (value: any): boolean => {
  if (!value) return true;
  if (typeof value === "string") {
    return value.trim() === "" && !value.startsWith("NEW:");
  }
  return false;
};

/**
 * Helper function to check if manually managed field has "NEW:" but no name
 */
export const hasEmptyNewFieldName = (value: any): boolean => {
  if (!value) return false;
  if (typeof value === "string") {
    return value === "NEW:" || value.trim() === "NEW:";
  }
  return false;
};

/**
 * Field type mappings used in solution imports
 */
export const FIELD_TYPE_MAPPINGS = {
  ApiConnection: "",
  ContactField: "ContactFields",
  ContactFieldOption: "",
  ContactSet: "ContactGroups",
  DataStructure: "",
  DataStructureField: "",
  ReportingTemplate: "ReportingTemplates",
  ReportingTemplateField: "",
  Topic: "ReportingTopics",
} as const;

/**
 * Generate a label for imported items
 * If customLabel starts with "NEW:", use the custom name
 * Otherwise, append a timestamp to the original label
 */
export const generateImportedLabel = (
  originalLabel: string,
  customLabel?: string
): string => {
  if (customLabel && customLabel.startsWith("NEW:")) {
    return customLabel.substring(4); // Remove "NEW:" prefix
  }
  return `${originalLabel} (Imported - ${new Date().toISOString()})`;
};

/**
 * Initialize empty external field updates structure
 */
export const createEmptyFieldUpdates = () => ({
  ContactField: {},
  ContactFieldOption: {},
  ContactSet: {},
  DataStructure: {},
  DataStructureField: {},
  ReportingTemplate: {},
  ReportingTemplateField: {},
  Topic: {},
  ApiConnection: {},
});

const parseJsonObject = (value: string): any | null => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
};

const withoutIdentity = (value: any): any => {
  if (!value || typeof value !== "object") return value;
  const { uuid, order, ...rest } = value;
  return rest;
};

export const getReportingTemplateByKey = (
  importedJSON: any,
  templateKey: string
): any | null => {
  return (
    importedJSON.Item?.ReportingTemplates?.find(
      (template: any) => template.uuid === templateKey
    ) || parseJsonObject(templateKey)
  );
};

export const getReportingTemplateFieldByKey = (
  template: any,
  fieldKey: string
): any | null => {
  const parsedField = parseJsonObject(fieldKey);

  return (
    template?.fields?.find((field: any) => {
      if (field.uuid === fieldKey) return true;
      if (JSON.stringify(withoutIdentity(field)) === fieldKey) return true;
      if (parsedField?.label && field.label === parsedField.label) return true;
      return false;
    }) || null
  );
};

export const getReportingTemplateFieldMappingUpdates = (
  sourceTemplate: any,
  targetTemplate: any,
  externalFields: Record<string, any> = {}
): Record<string, string> => {
  return Object.keys(externalFields).reduce((updates, fieldKey) => {
    const sourceField = getReportingTemplateFieldByKey(sourceTemplate, fieldKey);
    if (!sourceField) return updates;

    const targetField = targetTemplate?.fields?.find(
      (field: any) => field.label === sourceField.label
    );

    updates[fieldKey] = targetField?.uuid || "";
    return updates;
  }, {} as Record<string, string>);
};
